import { useRef, useState } from 'react'

import { useChartDimensions } from '@/components/ui/chart/Hooks'
import { ChartTooltip } from '@/components/ui/chart/Tooltip'
import { FlowHeadings } from './FlowHeadings'
import { FlowLabels } from './FlowLabels'
import { FlowLegend } from './FlowLegend'
import { FlowLinks } from './FlowLinks'
import { FlowNodes } from './FlowNodes'
import { FlowTooltip } from './FlowTooltip'
import { type FlowFocus, flowHighlight } from './flowFocus'
import type { FlowValueFormat } from './flowFormat'
import { type FlowLayout, type LaidOutFlowNode, layoutFlow } from './flowLayout'
import type { FlowOfFunds } from './flowModel'

interface FlowOfFundsChartProps {
  flow: FlowOfFunds
  valueFormat: FlowValueFormat
}

// The report's flow of funds Sankey, drawn from whatever columns the
// picked view has. Color follows the kind of flow.
export function FlowOfFundsChart({ flow, valueFormat }: FlowOfFundsChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { width, height } = useChartDimensions(
    containerRef as React.RefObject<HTMLDivElement>,
  )
  const [focus, setFocus] = useState<FlowFocus>(null)
  const layout = layoutFlow(flow, width, height)
  const highlight = flowHighlight(focus, edgesOf(layout))

  return (
    <div ref={containerRef} className="relative h-full w-full">
      {layout.nodes.length > 0 && (
        <svg
          role="img"
          aria-label="Flow of funds chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
        >
          <FlowLegend links={layout.links} />
          <FlowHeadings headings={layout.headings} valueFormat={valueFormat} />
          <FlowLinks
            links={layout.links}
            highlight={highlight}
            onFocus={setFocus}
          />
          <FlowNodes
            nodes={layout.nodes}
            highlight={highlight}
            onFocus={setFocus}
          />
          <FlowLabels
            labels={layout.labels}
            highlight={highlight}
            valueFormat={valueFormat}
          />
        </svg>
      )}
      <ChartTooltip
        active={focus !== null}
        position={tooltipAnchor(focus, layout)}
      >
        <FlowTooltip
          focus={focus}
          flow={flow}
          layout={layout}
          valueFormat={valueFormat}
        />
      </ChartTooltip>
    </div>
  )
}

function edgesOf(layout: FlowLayout) {
  return layout.links.map((link) => ({
    source: (link.source as LaidOutFlowNode).name,
    target: (link.target as LaidOutFlowNode).name,
  }))
}

function tooltipAnchor(focus: FlowFocus, layout: FlowLayout) {
  if (!focus) return undefined
  if (focus.type === 'node') {
    const node = layout.nodes.find((candidate) => candidate.name === focus.name)
    if (!node) return undefined
    return { x: ((node.x0 ?? 0) + (node.x1 ?? 0)) / 2, y: node.y0 ?? 0 }
  }
  const link = layout.links[focus.index]
  if (!link) return undefined
  const source = link.source as LaidOutFlowNode
  const target = link.target as LaidOutFlowNode
  return {
    x: ((source.x1 ?? 0) + (target.x0 ?? 0)) / 2,
    y: ((link.y0 ?? 0) + (link.y1 ?? 0)) / 2 - (link.width ?? 0) / 2,
  }
}
